import { useNextStep } from 'hooks/useStep';

import { useGetQuestions } from 'api/hooks/useSurveyQuestions';
import { AnswersItem, Maybe, QuestionItem } from '__generated__/graphql';
import QuestionPager from 'components/QuestionPager';
import Loading from 'components/Loading';
import { useMemo, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getProgress, Section } from '../persistence/localProgress';
import { useUser } from '../context/userContext';
import { readTimes, mergeTimes } from '../persistence/answerTimes';

interface QuestionsProps {
  id: keyof {
    personality: Maybe<QuestionItem>[];
    interests: Maybe<QuestionItem>[];
    values: Maybe<QuestionItem>[];
  };
  title?: string;
  icon?: string;
}

const Questions = ({ id, title, icon }: QuestionsProps) => {
  const { company, answers, answerTimes, saveUserAnswers, jobRoleId, clear } = useUser();
  const { questions, questionLoading } = useGetQuestions({ companyId: company?.id || '', jobRoleId });

  const { nextStep } = useNextStep();
  const navigate = useNavigate();
  const { companySlug } = useParams();

  const [progressTick, setProgressTick] = useState(0);

  const handleProgressUpdate = useCallback(() => {
    setProgressTick((k) => k + 1);
  }, []);

  const storeAnswers = async (newAnswers: AnswersItem) => {
    const storedAnswers: AnswersItem = { ...(answers || {}) } as AnswersItem;
    const answerKeys = Object.keys(newAnswers) as (keyof AnswersItem)[];
    answerKeys.forEach((key) => {
      storedAnswers[key] = newAnswers[key];
    });

    // Merge server answerTimes with locally persisted times to avoid overwriting
    const userId = localStorage.getItem('id') || undefined;
    const localTimes = readTimes(company?.id, userId);
    const serverAsMap = (answerTimes || {}) as unknown as Record<string, string[] | undefined>;
    const toTimesMap = (src: Record<string, string[] | undefined>) => {
      const result: Record<string, string[]> = {};
      Object.keys(src).forEach((k) => {
        const v = src[k];
        result[k] = v || [];
      });
      return result;
    };
    const mergedTimes = mergeTimes(toTimesMap(serverAsMap), localTimes);

    // Save merged answers and merged answerTimes
    saveUserAnswers(storedAnswers, mergedTimes as unknown as import('../__generated__/graphql').AnswerTimesInput);
    nextStep();
  };

  // Compute totals and answered counts across sections for the progress bar
  const totals: Record<Section, number> = useMemo(
    () => ({
      personality: (questions.personality || []).filter(Boolean).length,
      interests: (questions.interests || []).filter(Boolean).length,
      values: (questions.values || []).filter(Boolean).length,
    }),
    [questions],
  ) as Record<Section, number>;

  const answered = useMemo(() => {
    const companyId = company?.id;
    const sections: Section[] = ['personality', 'interests', 'values'];
    const counts = sections.reduce(
      (acc, s) => {
        const { answers: answersFromProgress } = getProgress(companyId, s);

        // Count from local progress (fast, persisted)
        const localCount = Object.keys(answersFromProgress || {}).length;

        // Count from server answers snapshot (fallback to capture last answer on navigation)
        const sectionIds = (questions[s] || [])
          .filter(Boolean)
          .map((q) => (q as Maybe<QuestionItem>)?.id)
          .filter(Boolean) as string[];
        const answersMap = (answers || {}) as unknown as Record<string, boolean | null>;
        const snapshotCount = sectionIds.reduce((n, qid) => (answersMap[qid] != null ? n + 1 : n), 0);

        acc[s] = Math.max(localCount, snapshotCount);
        return acc;
      },
      {} as Record<Section, number>,
    );
    return counts;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company?.id, progressTick, questions, answers]);

  const handleRestart = () => {
    if (companySlug) navigate(`/${companySlug}`);
    else navigate('/');
    clear();
  };

  // While questions are loading or not yet available for this section, show loader
  if (questionLoading || !questions[id]) {
    return (
      <div className="flex flex-col items-center justify-center gap-20 text-center text-white h-5/6">
        <div className="mb-1 text-xl font-semibold text-white sm:text-display-sm leading-xl sm:leading-display-sm">Loading your questions</div>
        <Loading isComponent={true} />
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-between text-center text-white h-5/6 w-full">
      <button
        type="button"
        onClick={handleRestart}
        className="fixed top-3 right-3 sm:top-4 sm:right-6 px-3 py-1 rounded-full bg-white/10 hover:bg-white/20 text-white text-xs sm:text-sm border border-white/20"
      >
        Restart
      </button>
      <img alt="questions-icon" className="hidden sm:block m-3 text-white w-12 h-12 sm:w-14 sm:h-14" src={icon} />
      <div className="mb-1 font-semibold text-white text-base sm:text-display-sm leading-xl sm:leading-display-sm">{title}</div>
      {questions[id]?.length === 0 ? (
        <div className="loading">
          <p>Sorry no questions have loaded for this session</p>
        </div>
      ) : (
        <QuestionPager
          questions={questions[id] || []}
          section={id as 'personality' | 'interests' | 'values'}
          onSectionComplete={storeAnswers}
          onProgressUpdate={handleProgressUpdate}
          totals={totals}
          answered={answered}
        />
      )}
    </div>
  );
};

export default Questions;
